import React, { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './RegistrationPage.css';
import { validateBusinessIdLoose } from '../utils/businessId';

const ALLOWED_CITIES = ['Espoo', 'Helsinki', 'Vantaa', 'Kauniainen', 'Kirkkonummi'];
const API_URL = (process.env.REACT_APP_API_URL || '').trim();
const IS_DEV = process.env.NODE_ENV !== 'production';

/* ---------- Validators ---------- */
// (MUUTETTU VAIN TÄMÄ FUNKTIO käyttämään utilia)
function isValidBusinessId(input) {
  if (!input) return true; // valinnainen
  const raw = String(input).trim().replace(/[–—−]/g, '-');
  // Jos ei FI-muotoinen (1234567-8), hyväksytään FE:ssä (muiden maiden tunnisteet)
  if (!/^\d{7}-?\d$/.test(raw)) return true;
  // FI: käytä utilia (mod-11 tarkiste)
  return validateBusinessIdLoose(raw).ok;
}
function isValidEmail(email) {
  if (!email) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}
function isValidPhone(phone) {
  if (!phone) return false;
  return /^[+\d][\d\s-]{5,}$/.test(phone);
}
function computeInvalids(values) {
  return {
    name: !values.name.trim(),
    email: !isValidEmail(values.email),
    phone: !isValidPhone(values.phone),
    streetAddress: values.streetAddress.replace(/\s+/g, ' ').trim().length < 3,
    postalCode: !/^\d{5}$/.test(values.postalCode),
    city: !ALLOWED_CITIES.includes(values.city),
    businessId: !!values.businessId && !isValidBusinessId(values.businessId),
  };
}
function messageForFirstInvalid(invalid) {
  if (invalid.name) return 'Nimi on pakollinen.';
  if (invalid.email) return 'Sähköpostiosoite on virheellinen.';
  if (invalid.phone) return 'Puhelinnumero on virheellinen. Käytä muotoa +358… tai kotimainen numero.';
  if (invalid.streetAddress) return 'Syötä katuosoite.';
  if (invalid.postalCode) return 'Postinumero tulee olla 5 numeroa (esim. 00100).';
  if (invalid.city) return 'Valitse toimipaikka listasta (Espoo, Helsinki, Vantaa, Kauniainen tai Kirkkonummi).';
  // (päivitetty viesti liittyy vain business ID -logiikkaan)
  if (invalid.businessId) return 'Y-tunnuksen tarkistus ei täsmää (muoto 1234567-8).';
  return '';
}

// Kenttävirheiden nosto ja mahdollinen remapping (street -> streetAddress)
function extractFieldErrors(err) {
  const data = err?.response?.data;
  const fieldErrors = {};
  let generalError = '';

  if (Array.isArray(data?.errors)) {
    for (const e of data.errors) {
      if (e?.param && e?.msg) fieldErrors[e.param] = e.msg;
    }
  } else if (data?.errors && typeof data.errors === 'object') {
    Object.entries(data.errors).forEach(([k, v]) => {
      fieldErrors[k] = typeof v === 'string' ? v : String(v);
    });
  } else if (typeof data === 'string') {
    generalError = data;
  } else if (data?.error || data?.message) {
    generalError = data.error || data.message;
  } else if (err?.message) {
    generalError = err.message;
  }

  // Remap mahdollisesta "street" -> "streetAddress"
  if (fieldErrors.street && !fieldErrors.streetAddress) {
    fieldErrors.streetAddress = fieldErrors.street;
    delete fieldErrors.street;
  }

  return { fieldErrors, generalError };
}

/* ---------- Component ---------- */
export default function RegistrationPage() {
  const navigate = useNavigate();

  const [values, setValues] = useState({
    name: '',
    email: '',
    phone: '',
    streetAddress: '',
    postalCode: '',
    city: 'Espoo',
    businessId: '',
  });
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Dev-debug
  const [debugResponse, setDebugResponse] = useState(null);
  const [debugPayload, setDebugPayload] = useState(null);

  const refs = {
    name: useRef(null),
    email: useRef(null),
    phone: useRef(null),
    streetAddress: useRef(null),
    postalCode: useRef(null),
    city: useRef(null),
    businessId: useRef(null),
  };

  const invalid = useMemo(() => computeInvalids(values), [values]);
  const clientValidationError = useMemo(() => messageForFirstInvalid(invalid), [invalid]);
  const isApiConfigured = Boolean(API_URL);

  // johdetut booleanit aria-invalidille
  const isNameInvalid          = invalid.name || !!fieldErrors.name;
  const isEmailInvalid         = invalid.email || !!fieldErrors.email;
  const isPhoneInvalid         = invalid.phone || !!fieldErrors.phone;
  const isStreetAddressInvalid = invalid.streetAddress || !!fieldErrors.streetAddress;
  const isPostalInvalid        = invalid.postalCode || !!fieldErrors.postalCode;
  const isCityInvalid          = invalid.city || !!fieldErrors.city;
  const isBusinessIdInvalid    = invalid.businessId || !!fieldErrors.businessId;

  const fieldOrder = ['name','email','phone','streetAddress','postalCode','city','businessId'];
  const focusFirstInvalid = (preferFieldErrors = false) => {
    const source = preferFieldErrors ? fieldErrors : invalid;
    for (const key of fieldOrder) {
      if (preferFieldErrors ? !!source[key] : source[key]) {
        const el = refs[key]?.current;
        if (el && typeof el.focus === 'function') {
          el.focus({ preventScroll: true });
          el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
        break;
      }
    }
  };

  const setValue = (name, value) => {
    setValues((v) => ({ ...v, [name]: value }));
    setFieldErrors((prev) => ({ ...prev, [name]: '' }));
  };
  const handleChange = (e) => {
    const { name, value } = e.target;
    setValue(name, value);
  };
  const handlePostalChange = (e) => {
    const onlyDigits = e.target.value.replace(/\D/g, '').slice(0, 5);
    setValue('postalCode', onlyDigits);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setFieldErrors({});
    if (IS_DEV) { setDebugPayload(null); setDebugResponse(null); }

    if (!isApiConfigured) {
      setError('Järjestelmäasetus puuttuu: REACT_APP_API_URL ei ole määritelty. Ota yhteys ylläpitoon.');
      return;
    }
    if (clientValidationError) {
      setError(clientValidationError);
      focusFirstInvalid(false);
      return;
    }

    setSubmitting(true);
    try {
      const norm = {
        name: values.name.trim(),
        email: values.email.trim().toLowerCase(),
        phone: values.phone.trim(),
        streetAddress: values.streetAddress.replace(/\s+/g, ' ').trim(),
        postalCode: values.postalCode.trim(),
        city: values.city,
        // (MUUTETTU: businessId normalisoidaan utilin avulla jos annettu)
        ...(values.businessId.trim()
          ? (() => {
              const v = validateBusinessIdLoose(values.businessId);
              return { businessId: v.ok ? (v.normalized || values.businessId.trim()) : values.businessId.trim() };
            })()
          : {}),
      };

      // Rakennetaan myös yksi rivillinen osoite (mallissa on address: String)
      const addressLine = `${norm.streetAddress}, ${norm.postalCode} ${norm.city}`;

      // Lähetetään mallin mukaiset kentät + address-string varmuuden vuoksi
      const payload = { ...norm, address: addressLine };

      if (IS_DEV) setDebugPayload(payload);

      await axios.post(`${API_URL}/api/users/register`, payload, {
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        timeout: 20000,
      });

      navigate('/success?from=registration');
    } catch (err) {
      if (IS_DEV) {
        setDebugResponse({
          status: err?.response?.status ?? '(no status)',
          data: err?.response?.data ?? err?.message ?? '(no data)',
        });
        // eslint-disable-next-line no-console
        console.error('Registration error:', err?.response?.status, err?.response?.data || err.message);
      }

      if (err?.response?.status === 409) {
        setFieldErrors({ email: 'Tällä sähköpostilla on jo tili.' });
        setError('Sähköpostiosoite on jo käytössä.');
        focusFirstInvalid(true);
      } else {
        const { fieldErrors: fe, generalError } = extractFieldErrors(err);
        if (Object.keys(fe).length > 0) {
          setFieldErrors(fe);
          focusFirstInvalid(true);
        }
        setError(
          generalError ||
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          'Rekisteröinti epäonnistui. Tarkista kentät ja yritä uudelleen.'
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="reg-container">
      {!isApiConfigured && (
        <div className="reg-alert reg-alert-error">
          <strong>Huom!</strong> REACT_APP_API_URL ei ole asetettu. Lomakkeen lähetys ei voi onnistua.
        </div>
      )}

      <h1 className="reg-title">Luo tili ja yritystunnus</h1>
      <p className="reg-subtitle">
        Täytä tiedot alle. Saat sähköpostiisi vahvistuslinkin, jonka kautta aktivoit tunnuksen ja voit kirjautua sisään.
      </p>

      <form onSubmit={handleSubmit} noValidate className="reg-form">
        {/* Nimi */}
        <div className="reg-field">
          <label htmlFor="name" className="reg-label">Yrityksen nimi *</label>
          <input
            id="name"
            name="name"
            ref={refs.name}
            type="text"
            required
            value={values.name}
            onChange={handleChange}
            className="reg-input"
            placeholder="Yritys Oy "
            aria-invalid={isNameInvalid}
          />
          {fieldErrors.name && <div className="reg-field-error">{fieldErrors.name}</div>}
        </div>

        {/* Sähköposti */}
        <div className="reg-field">
          <label htmlFor="email" className="reg-label">Sähköposti *</label>
          <input
            id="email"
            name="email"
            ref={refs.email}
            type="email"
            required
            value={values.email}
            onChange={handleChange}
            className="reg-input"
            placeholder="nimi@yritys.fi"
            aria-invalid={isEmailInvalid}
          />
          <p className="reg-help">Varmista, että pääset lukemaan tähän osoitteeseen tulevan viestin.</p>
          {fieldErrors.email && <div className="reg-field-error">{fieldErrors.email}</div>}
        </div>

        {/* Puhelin */}
        <div className="reg-field">
          <label htmlFor="phone" className="reg-label">Puhelin *</label>
          <input
            id="phone"
            name="phone"
            ref={refs.phone}
            type="tel"
            required
            value={values.phone}
            onChange={handleChange}
            className="reg-input"
            placeholder="+358 40 123 4567"
            aria-invalid={isPhoneInvalid}
          />
          {fieldErrors.phone && <div className="reg-field-error">{fieldErrors.phone}</div>}
        </div>

        {/* Katuosoite */}
        <div className="reg-field">
          <label htmlFor="streetAddress" className="reg-label">Katuosoite *</label>
          <input
            id="streetAddress"
            name="streetAddress"
            ref={refs.streetAddress}
            type="text"
            required
            value={values.streetAddress}
            onChange={handleChange}
            className="reg-input"
            placeholder="Esimerkkikatu 1 A 1"
            aria-invalid={isStreetAddressInvalid}
          />
          {fieldErrors.streetAddress && <div className="reg-field-error">{fieldErrors.streetAddress}</div>}
        </div>

        {/* Postinumero + Kaupunki */}
        <div className="reg-row">
          <div className="reg-col">
            <label htmlFor="postalCode" className="reg-label">Postinumero *</label>
            <input
              id="postalCode"
              name="postalCode"
              ref={refs.postalCode}
              inputMode="numeric"
              pattern="\d{5}"
              required
              value={values.postalCode}
              onChange={handlePostalChange}
              className="reg-input"
              placeholder="00100"
              aria-invalid={isPostalInvalid}
            />
            {fieldErrors.postalCode && <div className="reg-field-error">{fieldErrors.postalCode}</div>}
          </div>

          <div className="reg-col">
            <label htmlFor="city" className="reg-label">Toimipaikka (kunta) *</label>
            <select
              id="city"
              name="city"
              ref={refs.city}
              required
              value={values.city}
              onChange={handleChange}
              className="reg-select"
              aria-invalid={isCityInvalid}
            >
              {ALLOWED_CITIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            <p className="reg-help">
              Palvelemme tällä hetkellä alueilla: {ALLOWED_CITIES.join(', ')}.
            </p>
            {fieldErrors.city && <div className="reg-field-error">{fieldErrors.city}</div>}
          </div>
        </div>

        {/* Y-tunnus (valinnainen) */}
        <div className="reg-field">
          <label htmlFor="businessId" className="reg-label">Y-tunnus (valinnainen)</label>
          <input
            id="businessId"
            name="businessId"
            ref={refs.businessId}
            type="text"
            value={values.businessId}
            onChange={handleChange}
            className="reg-input"
            placeholder="1234567-8"
            aria-invalid={isBusinessIdInvalid}
          />
          <p className="reg-help">Jätä tyhjäksi, jos et tiedä Y-tunnusta vielä.</p>
          {fieldErrors.businessId && <div className="reg-field-error">{fieldErrors.businessId}</div>}
        </div>

        {/* Yleiset ilmoitukset */}
        {clientValidationError && !error && (
          <div className="reg-alert reg-alert-warn">{clientValidationError}</div>
        )}
        {error && (
          <div className="reg-alert reg-alert-error">{error}</div>
        )}

        {/* Toiminnot */}
        <div className="reg-actions">
          <button
            type="submit"
            disabled={submitting}
            className={`reg-btn reg-btn-primary${submitting ? ' is-disabled' : ''}`}
          >
            {submitting ? 'Luodaan…' : 'Luo tunnus'}
          </button>
          <p className="reg-footnote">
            Painamalla “Luo tunnus” hyväksyt, että lähetämme sinulle aktivointiviestin.
          </p>
        </div>

        {/* Dev-debug (ei näy tuotannossa) */}
        {IS_DEV && (debugResponse || debugPayload) && (
          <div className="reg-alert reg-alert-warn" style={{ marginTop: 16 }}>
            <strong>Debug details:</strong>
            {debugPayload && (
              <>
                <div style={{ marginTop: 8 }}>Payload sent:</div>
                <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {JSON.stringify(debugPayload, null, 2)}
                </pre>
              </>
            )}
            {debugResponse && (
              <>
                <div>Server response:</div>
                <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {JSON.stringify(debugResponse, null, 2)}
                </pre>
              </>
            )}
          </div>
        )}
      </form>
    </div>
  );
}
